import { FC, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { CaseIcon } from '../../../components/Icons'
import { useMentorsState } from '../../../hooks/storeHooks/useMentors'
import { getTimeAgo } from '../../../utils/getTimeAgo'
import {
  uploadImage,
  useFetchFilters,
  useUpdateCompanion,
} from '../../api/apiHooks'
import { IMentor } from '../../api/types'
import { MentorEditForm, MentorEditFormProps } from '../MentorEditForm'
import { Modal } from '../Modal/Modal'
import { TreeDotsDropdown } from '../TreeDotsDropdown'
const blackImage = '/images/black.jpeg'

interface MentorCardAdminProps {
  profile: IMentor
}

export const MentorCardAdmin: FC<MentorCardAdminProps> = ({ profile }) => {
  const { setSelectedMentor } = useMentorsState()
  const queryClient = useQueryClient()
  const { data: allFilters } = useFetchFilters()
  const { mutateAsync: updateCompanion } = useUpdateCompanion()
  const [isOpenEditModal, setIsOpenEditModal] = useState(false)

  const handleCardClick = () => {
    setSelectedMentor(profile)
  }

  const handleOpenEditModal = () => {
    setIsOpenEditModal(true)
  }

  const handleCloseEditModal = () => {
    setIsOpenEditModal(false)
  }

  const handleEditModal = async (
    data: MentorEditFormProps['value'] & { imageUrl?: string },
  ) => {
    try {
      if (!data.mentorId) {
        toast.error('Malformed request. Please try again.')
        return
      }

      if (data.previewImageFile) {
        // Upload new image and get URL
        const response = await uploadImage(data.previewImageFile)
        data.imageUrl = response.config?.url?.split('?')[0]
        delete data.previewUrl
        delete data.previewImageFile
      }
      console.log('Updating companion with data:', data)
      const { updateMentor } = await updateCompanion(data)
      if (updateMentor?.success) {
        await queryClient.invalidateQueries({ queryKey: ['get-all-mentors'] })
        toast.success('Companion updated successfully')
        setIsOpenEditModal(false)
      } else {
        toast.error('Failed to update companion. Please try again.')
      }
    } catch (error) {
      console.error('Error updating companion:', error)
      toast.error('Failed to update companion. Please try again.')
    }
  }

  const sessionsLabel = useMemo(() => {
    if (profile.statistics?.sessionsCount === 0) {
      return 'No sessions'
    }
    if (profile.statistics?.sessionsCount === 1) {
      return '1 session'
    }
    return `${profile.statistics?.sessionsCount} sessions`
  }, [profile.statistics?.sessionsCount])

  return (
    <>
      <div className="relative tc-mv-card-item">
        <div className="absolute top-3 right-3 z-10">
          <TreeDotsDropdown
            mentor={profile}
            onEditMenuClick={handleOpenEditModal}
          />
        </div>
        <Link
          to={`/${profile?.slug}?start=true`}
          state={{
            prevLocation: `${window.location.pathname}${window.location.search}`,
          }}
          onClick={handleCardClick}
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            className="tc-mv-card-bg-img"
            src={profile?.previewUrl || blackImage}
            alt="Mentor"
          />
          <div className="tc-mv-avatar-details">
            <div className="mb-2 tc-mv-avatar-name capitalize">{`${profile.firstName} ${profile.lastName}`}</div>
            <div
              className="line-clamp-2 mb-1 tc-mv-avatar-designation"
              title={profile.headline}
            >
              {profile.headline}
            </div>
            <div className="flex-wrap text-dark-neutral-400 text-sm gap-2 hidden items-center md:flex whitespace-nowrap">
              <div className="flex gap-1 items-center">
                <CaseIcon size={14} />
                {profile.jobTitle}
              </div>
            </div>
            <div className="text-[#F2F2F4] flex items-center gap-1 text-xs mt-1">
              {sessionsLabel}
              <div className="bg-dark-neutral-400 h-[4px] rounded-full w-[4px]" />
              {getTimeAgo(profile.createdAt)}
            </div>
          </div>
        </Link>
      </div>
      <Modal
        title="Edit companion"
        onClose={handleCloseEditModal}
        open={isOpenEditModal}
      >
        <MentorEditForm
          id={isOpenEditModal ? profile.mentorId : ''}
          value={profile}
          onModalClose={handleCloseEditModal}
          onSubmit={handleEditModal}
          filters={allFilters}
        />
      </Modal>
    </>
  )
}
